import styled from '@emotion/styled';
import { TooltipWhite } from '@root/Design/Primitives';
import React, { Fragment } from 'react';
import { CSSProperties } from 'react';

export type RichTextRun = { text: string; type: 'variable' | 'explicit'; varKey?: string; info?: RichTextItem };
export type RichTextRuns = string | RichTextRun | Array<RichTextRun | string>;
export type RichTextList = { items: Array<RichTextRuns>; type?: 'numeric' | 'bullet' };
export type RichTextLines = { lines: Array<RichTextRuns | RichTextList> };
export type RichTextItem = RichTextRuns | RichTextList | RichTextLines;

export function* iterateRtText(text: RichTextItem, includeInfo: boolean = false): Iterable<string> {
    const items: Array<string | RichTextRun | RichTextList | RichTextLines> = text instanceof Array ? text.slice() : [text];

    while (items.length) {
        let item = items.shift()!;
        if (typeof item === 'string') {
            yield item;
        } else if ('items' in item) {
            items.unshift(...item.items.flatMap((i) => (i instanceof Array ? i : [i])));
        } else if ('lines' in item) {
            items.unshift(...item.lines.flatMap((l) => (l instanceof Array ? l : [l])));
        } else if ('text' in item) {
            yield item.text;
            if (includeInfo && item.info) {
                const info = item.info;
                items.unshift(...(info instanceof Array ? info : [info]));
            }
        }
    }
}
export function findRtText(text: RichTextItem, callback: (text: string) => boolean) {
    for (const item of iterateRtText(text)) {
        if (callback(item)) {
            return item;
        }
    }
    return false;
}

type ColorGenerator = (item: RichTextRun) => string;
function createColorGenerator() {
    const variableColors = ['#009FE1', '#00A79D', '#5C4B8C', '#d47800', '#e02200'];

    return (() => {
        let varCt = 0;
        const keyColors = new Map<string, string>();
        const nextColor = () => variableColors[varCt++ % variableColors.length];
        return (item: RichTextRun) => {
            if (item.varKey) {
                if (!keyColors.has(item.varKey)) {
                    keyColors.set(item.varKey, nextColor());
                }
                return keyColors.get(item.varKey)!;
            } else {
                return nextColor();
            }
        };
    })();
}

function RichTextRenderer(props: { text: RichTextItem; genColor: (item: RichTextRun) => string }) {
    const { genColor, text } = props;

    const items = text instanceof Array ? text : [text];

    return (
        <>
            {items.map((item, i) => {
                if (typeof item === 'string') {
                    return <RtRun key={i}>{item}</RtRun>;
                } else if ('items' in item) {
                    const Nl = item.type === 'numeric' ? 'ol' : 'ul';
                    return (
                        <Nl key={i}>
                            {item.items.map((item, ii) => (
                                <li key={ii}>
                                    <RichTextRenderer text={item} genColor={genColor} />
                                </li>
                            ))}
                        </Nl>
                    );
                } else if ('type' in item) {
                    let styles: CSSProperties = { margin: '0 2px' };
                    const el =
                        item.type === 'variable' ? (
                            <RtVar key={i} color={genColor(item)}>
                                {item.text}
                            </RtVar>
                        ) : (
                            <RtRun key={i} style={styles}>
                                {item.text}
                            </RtRun>
                        );

                    return item.info ? (
                        <TooltipWhite key={i} label={<RichTextRenderer text={item.info} genColor={genColor} />}>
                            {el}
                        </TooltipWhite>
                    ) : (
                        el
                    );
                } else if ('lines' in item) {
                    return (
                        <Fragment key={i}>
                            {item.lines.map((line, ii) => {
                                return (
                                    <RtLine key={ii}>
                                        <RichTextRenderer text={line} genColor={genColor} />
                                    </RtLine>
                                );
                            })}
                        </Fragment>
                    );
                }
            })}
        </>
    );
}

export const RichText = React.memo(
    function (props: { text: RichTextItem }) {
        const genColor = createColorGenerator();
        return <RichTextRenderer text={props.text} genColor={genColor} />;
    },
    (prev, next) => {
        return JSON.stringify(prev.text) === JSON.stringify(next.text);
    }
);

const RtRun = styled.span`
    margin: 0 2px;
`;

const RtVar = styled(RtRun)<{ color: string }>`
    color: ${(p) => p.color};
    background: #0001;
    border-radius: 2px;
`;

const RtLine = styled.div`
    display: block;
`;
